import { FC } from "react";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import { Box, Chip, Typography } from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { textColorState, tradingIdeaAvailabilityState } from "@/lib/store";
import { Stock } from "@/models/stock";

interface Props {
    stock: Stock;
    // Tighter variant for the mobile slot under the company name;
    // desktop uses the Chip alongside the market-hours indicator.
    compact?: boolean;
}

/**
 * "Trading Idea Issued" pill in the page top-bar, sibling to
 * `<StockReportBadge/>`. A stock can carry both, and when it does they
 * sit next to each other.
 *
 * Reads `tradingIdeaAvailabilityState`, populated by `RootLayout` when
 * a stock page mounts. That probe is gated server-side, so a user
 * without the product group sees `exists: false` and this renders
 * nothing — the same as a stock with no idea. There is no client-side
 * entitlement check here on purpose: one gate, in one place, that
 * cannot be forgotten at a call site.
 *
 * Renders nothing rather than placeholder space, so the top bar does
 * not jump when the probe resolves.
 *
 * Deliberately no "New" dot, unlike the Stock Report badge. Ideas are
 * published daily and a fresh one is the normal case, so a permanent
 * "New" would be noise; Stock Reports are weekly, where recency
 * genuinely distinguishes.
 */
const TradingIdeaBadge: FC<Props> = ({ stock, compact = false }) => {
    const router = useRouter();
    const textColor = useRecoilValue(textColorState);
    const availability = useRecoilValue(tradingIdeaAvailabilityState);

    if (!availability || !availability.exists) return null;
    if (!stock?.ticker || !stock?.exchangesymbol) return null;

    const target = availability.deepLinkPath || `/s/${stock.exchangesymbol}/${stock.ticker}/trading-idea`;
    const label = "Trading Idea Issued";

    if (compact) {
        return (
            <Box
                onClick={() => router.push(target)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && router.push(target)}
                aria-label={label}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    mt: 0.75,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "primary.main",
                    color: "primary.main",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "primary.main", color: textColor },
                }}
            >
                <InsightsIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.3 }}>
                    {label}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 14, ml: 0.25 }} />
            </Box>
        );
    }

    return (
        <Chip
            size="small"
            variant="outlined"
            color="primary"
            clickable
            icon={<InsightsIcon sx={{ fontSize: 14 }} />}
            label={label}
            onClick={() => router.push(target)}
            aria-label={label}
            sx={{ fontWeight: 600, letterSpacing: 0.3 }}
        />
    );
};

export default TradingIdeaBadge;
